import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

@Component({
  selector: 'app-survey-star-rating',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './survey-star-rating.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './survey-star-rating.component.scss',
})
export class SurveyStarRatingComponent implements OnInit, OnChanges {
  @Input() maxStars = 5;
  @Input() initialStars = 0;
  @Input() readonly = false;
  @Input() wholeStars = true;
  @Input() color = 'crimson';
  @Output() ratingChange = new EventEmitter<number>();

  protected stars: number[] = [];
  protected rating = 0;

  ngOnInit(): void {
    this.refreshStars();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refreshStars();
  }

  private refreshStars(): void {
    this.stars = this.createStars(this.maxStars);
    this.rating = this.clampRating(this.initialStars);
  }

  protected onSelect(starNumber: number): void {
    if (this.readonly) {
      return;
    }

    this.rating = this.clampRating(starNumber);
    this.ratingChange.emit(this.rating);
  }

  protected isFilled(starNumber: number): boolean {
    return starNumber <= this.rating;
  }

  protected isSelected(starNumber: number): boolean {
    return starNumber === this.rating;
  }

  protected starAriaLabel(starNumber: number): string {
    return `${starNumber} ${starNumber === 1 ? 'star' : 'stars'}`;
  }

  protected getStarTabIndex(starNumber: number): number {
    if (this.readonly) {
      return -1;
    }

    if (this.rating <= 0) {
      return starNumber === 1 ? 0 : -1;
    }

    return this.isSelected(starNumber) ? 0 : -1;
  }

  protected starColor(): string {
    return this.color || 'crimson';
  }

  private createStars(maxStars: number): number[] {
    const safeMax = Number.isFinite(maxStars) ? Math.max(1, Math.floor(maxStars)) : 5;
    return Array.from({ length: safeMax }, (_, index) => index + 1);
  }

  private clampRating(value: number): number {
    const safeValue = Number.isFinite(value) ? value : 0;
    const roundedValue = this.wholeStars ? Math.round(safeValue) : Math.round(safeValue * 2) / 2;
    return Math.max(0, Math.min(this.stars.length || 5, roundedValue));
  }
}
